import React, {
	FormEvent,
	ReactNode,
	useEffect,
	useRef,
	useState
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import type { RootState } from '@/rootReducer';
import { sendAnalytics } from '@/services/analyticsService';
import './AddUserModal.scss';
import { MarkerLocationIcon } from '@/assets/images';
import { userToggleAddUserModal } from '../userActions';
import NewUserItem from './NewUserItem';
import type { User } from '../userTypes';

type Props = {
	user: User;
};

export default function AddUserModal({}: Props): JSX.Element {
	const [addUserModalOpen, user, addUserLoading] = useSelector<
		RootState,
		[any, any, any]
	>(
		(state: RootState) => [
			state.user.getIn(['authorize', 'addUserModalOpen']),
			state.user.getIn(['authorize', 'user']).toJS(),
			state.user.getIn(['addUser', 'loading'])
		],
		shallowEqual
	);

	const dispatch = useDispatch();
	const toggleAddUserModal = (payload: boolean) =>
		dispatch(userToggleAddUserModal(payload));

	const closeModal = () => {
		changeTab('initial');
		toggleAddUserModal(false);
		setCurrentCount(1);
		setNewUserArray([]);

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User closed Add a New User Modal`,
				properties: {
					array: newUserArray
				}
			});
		}
	};

	const [currentTab, setCurrentTab] = useState('initial');
	const [currentCount, setCurrentCount] = useState(1);
	const [newUserArray, setNewUserArray] = useState([]);

	const changeTab = tab => {
		if (currentTab === 'initial') {
			createNewUserArray(currentCount);
			setScrollToTop();
		}
		setCurrentTab(tab);

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User switched to ${tab} tab`,
				properties: {
					number_of_users: currentCount
				}
			});
		}
	};

	const setScrollToTop = () => {
		setTimeout(() => {
			const scrollEl = document.getElementsByClassName('addUserModal')[0];
			if (scrollEl) {
				scrollEl.scrollTop = 0;
			}
		}, 15);
	};

	const changeCount = count => {
		if (count[1] == 'decrement-count') {
			if (count[0] > 1) {
				setCurrentCount(count[0] - 1);
			}
		} else if (count[0] < 10) {
			setCurrentCount(count[0] + 1);
		}
	};

	const createNewUserArray = count => {
		if (!newUserArray) {
			const userArray = [];
			let x = 1;
			while (x <= count) {
				userArray[x - 1] = {
					userNumber: `User ${x.toString()}`,
					firstName: '',
					lastName: '',
					title: '',
					department: '',
					emailAddress: '',
					role: '',
					errors: null
				};
				x += 1;
			}
			setNewUserArray(userArray);
			return newUserArray;
		}

		const x = count;
		let numValues = count - newUserArray.length;
		if (numValues < 0) {
			const reducedUserArray = newUserArray.slice(
				0,
				newUserArray.length - numValues * -1
			);
			setNewUserArray(reducedUserArray);
			return reducedUserArray;
		}
		if (numValues > 0) {
			while (numValues > 0) {
				newUserArray.push({
					userNumber: `User ${(count - numValues + 1).toString()}`,
					firstName: '',
					lastName: '',
					title: '',
					department: '',
					emailAddress: '',
					role: '',
					errors: null
				});
				numValues -= 1;
			}
			setNewUserArray(newUserArray);
			return newUserArray;
		}
	};

	return (
		<>
			{addUserModalOpen ? (
				<div className="outermost-wrapper">
					<div className="addUserModal-bg" onClick={closeModal} />
					{currentTab === 'initial' && (
						<div className="addUserModal-outer">
							<div
								className={`addUserModal boom ${addUserLoading === true ? 'loading-modal' : ''}`}
							>
								<div>
									<div className="Select-number-of-new">
										Select number of new users to be added
									</div>
									<div className="select-number-of-users">
										Select number of users
									</div>
									<div className="counterButton">
										<div
											className="decrement-count"
											onClick={() =>
												changeCount([
													currentCount,
													'decrement-count'
												])
											}
										>
											-
										</div>
										<span className="count">
											{currentCount}
										</span>
										{currentCount == 10 && (
											<>
												<div
													className="increment-count-max"
													onClick={() =>
														changeCount([
															currentCount,
															'increment-count'
														])
													}
												>
													+
													<div className="max-hovertext-container">
														<div className="max-hovertext">
															To add more than 10
															people please reach
															out to Customer
															Success
														</div>
													</div>
												</div>
											</>
										)}
										{currentCount < 10 && (
											<div
												className="increment-count"
												onClick={() =>
													changeCount([
														currentCount,
														'increment-count'
													])
												}
											>
												+
											</div>
										)}
									</div>
									<div className="new-user-access-info">
										<div className="marker-location-holder">
											<MarkerLocationIcon className="marker-location" />
										</div>
										<div className="new-user-access-paragraph">
											<span className="cs-paragraph">
												New Users will have access to
												the locations and market that
												your account has access to.
											</span>{' '}
											<br />
											<br />
											<span className="cs-paragraph">
												To add Users with access to new
												locations and / or markets,
												reach out to&nbsp;
												<a
													className="email-link"
													href="mailto:support@re2.ai"
													target="_blank"
													color="blue"
													rel="noreferrer"
												>
													Customer Success
												</a>
											</span>
										</div>
									</div>
									<div className="customer-success-process">
										<span>
											Our Customer Success team will take
											care of adding this User to your
											Account and change your billing
											accordingly.
										</span>{' '}
										<br />
										<br />
										<span>
											New users will be billed at the same
											rate as current users, new billing
											will be effective as of the date the
											account is created.
										</span>
									</div>
								</div>
							</div>
							<div className="bottom-buttons">
								<button
									type="submit"
									className="cancel-button"
									onClick={closeModal}
								>
									Cancel
								</button>
								<button
									className="submit-button"
									type="submit"
									onClick={() => changeTab('showUsers')}
								>
									Enter User Details
								</button>
							</div>
						</div>
					)}
					{currentTab === 'showUsers' && (
						<>
							<div
								className={`addUserModal boom ${addUserLoading === true ? 'loading-modal' : ''}`}
							>
								<div>
									<NewUserItem
										closeModal={closeModal}
										setNewUserArray={setNewUserArray}
										newUserArray={newUserArray}
										addUserModalOpen={addUserModalOpen}
										userToggleAddUserModal={
											userToggleAddUserModal
										}
										changeTab={changeTab}
										user={user}
									/>
								</div>
							</div>
						</>
					)}
				</div>
			) : (
				''
			)}
		</>
	);
}
